import React, { useState, useEffect } from 'react';
import { supabase } from '../../../shared/lib/supabase';
import { Compass, AlertCircle, CheckCircle, Mail, Users } from 'lucide-react';
import { toast } from 'sonner';

interface TeamInvitationData {
  id: any;
  invited_email: any;
  teams: {
    name: any;
  };
}

export const CustomSignupPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteValid, setInviteValid] = useState(false);
  const [signupMethod, setSignupMethod] = useState<'email' | 'google' | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [inviteType, setInviteType] = useState<'general' | 'team'>('general');
  const [teamName, setTeamName] = useState<string>('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountCreated, setAccountCreated] = useState(false);

  // Get the token from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const type = urlParams.get('type');
  const email = urlParams.get('email') || '';

  // Password validation functions
  const passwordRequirements = {
    length: password.length >= 12,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  useEffect(() => {
    // Check if we have a token (from either URL pattern)
    if (token) {
      validateInvite();
    } else {
      setError('No invitation token found');
      setLoading(false);
    }
  }, [token]);

  const validateInvite = async () => {
    try {
      // First, check for team invitations (if email and type=invite are provided)
      if (email && type === 'invite') {
        const { data: teamInvitation, error } = await supabase
          .from('team_invitations')
          .select(`
            id,
            invited_email,
            teams!inner (name)
          `)
          .eq('invitation_token', token)
          .eq('invited_email', email)
          .eq('status', 'pending')
          .single();

        if (teamInvitation && !error) {
          const invitation = teamInvitation as any;
          setInviteType('team');
          setTeamName(invitation.teams.name);
          setInviteEmail(email);
          setInviteValid(true);
          setLoading(false);
          return;
        }
      }
      
      // Check for general invitation tokens (from your email system)
      const { data: generalInvitation, error: generalError } = await supabase
        .from('user_invitation_tokens')
        .select('email, expires_at, used')
        .eq('token', token)
        .eq('used', false)
        .single();

      if (generalInvitation && !generalError) {
        // Check if token has expired
        if (new Date(generalInvitation.expires_at) < new Date()) {
          setError('This invitation has expired');
          setLoading(false);
          return;
        }

        // Valid general invitation
        setInviteType('general');
        setInviteEmail(generalInvitation.email);
        setInviteValid(true);
        setLoading(false);
        return;
      }
      
      // If neither type of invitation found, it's invalid
      setError('Invalid or expired invitation');
      setLoading(false);
    } catch (err) {
      console.error('Error validating invitation:', err);
      setError('Invalid or expired invite link');
      setLoading(false);
    }
  };

  const handleEmailSignup = async () => {
    if (!inviteEmail.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }

    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use your existing complete_invitation function to create the user
      const { data: invitationResult, error: invitationError } = await supabase
        .rpc('complete_invitation', {
          invitation_token: token,
          user_password: password
        });

      if (invitationError) {
        setError('Error creating account: ' + invitationError.message);
        return;
      }

      // Parse the result
      const result = typeof invitationResult === 'string' 
        ? JSON.parse(invitationResult) 
        : invitationResult;

      if (!result.success) {
        setError('Error creating account: ' + (result.error || 'Unknown error'));
        return;
      }

      // Account created successfully - sign out to force manual login
      await supabase.auth.signOut();
      toast.success('Account created successfully!');
      setAccountCreated(true);

    } catch (err) {
      console.error('Signup error:', err);
      setError('Error creating account');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/?google_signup=true&token=${token}`
        }
      });
      
      if (error) {
        setError('Error signing up with Google: ' + error.message);
      }
    } catch (err) {
      setError('Error signing up with Google');
    }
  };

  const handleGoToLogin = () => {
    window.location.href = `/?email=${encodeURIComponent(inviteEmail)}`;
  };

  // Listen for auth changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          const urlParams = new URLSearchParams(window.location.search);
          const isGoogleSignup = urlParams.get('google_signup') === 'true';
          
          if (isGoogleSignup && session.user.email === inviteEmail) {
            // Mark the invitation token as used for Google signup
            try {
              const { error: tokenError } = await supabase
                .from('user_invitation_tokens')
                .update({ used: true })
                .eq('token', token);

              if (tokenError) {
                console.error('Error updating token:', tokenError);
              }

              toast.success('Account created successfully! Welcome to anthrogen:drift!');
              setTimeout(() => {
                window.location.href = '/';
              }, 1500);
            } catch (error) {
              console.error('Error in Google signup flow:', error);
            }
          } else if (!isGoogleSignup) {
            // This is for other auth flows
            if (session.user.email === inviteEmail) {
              window.location.href = `/complete-signup?token=${token}`;
            } else {
              toast.error('Please sign in with the email address that received the invitation.');
            }
          }
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [token, inviteEmail]);

  // Show account created success page
  if (accountCreated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="glass-card rounded-sm p-8 shadow-xl text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Account Created!</h2>
            <p className="text-gray-600 mb-2">
              Your account has been successfully created with:
            </p>
            <p className="font-semibold text-gray-800 mb-6">
              {inviteEmail}
            </p>
            <p className="text-sm text-gray-500 mb-8">
              You can now sign in using your email and the password you just created.
            </p>
            <button
              onClick={handleGoToLogin}
              className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 px-6 rounded-sm font-medium hover:from-green-700 hover:to-blue-700 transition-all duration-200"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="glass-card rounded-sm p-8 shadow-xl text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-sm hover:from-blue-700 hover:to-purple-700"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show email sent confirmation
  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="glass-card rounded-sm p-8 shadow-xl text-center">
            <Mail className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Check Your Email</h2>
            <p className="text-gray-600 mb-6">
              We've sent a password setup link to <strong>{inviteEmail}</strong>. 
              Click the link in your email to set up your password and complete your account setup.
            </p>
            <button
              onClick={() => setEmailSent(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              ← Back to signup options
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-gray-50">
      <div className="max-w-md w-full mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="glass-button rounded-sm p-3 mr-3">
              <Compass className="h-8 w-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              anthrogen:drift
            </h1>
          </div>
          <div className="glass-card rounded-sm p-4 mb-6">
            <div className="flex items-center justify-center mb-2">
              {inviteType === 'team' ? (
                <Users className="h-5 w-5 text-blue-500 mr-2" />
              ) : (
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              )}
              <span className="text-blue-700 font-medium">
                {inviteType === 'team' ? 'Team Invitation' : 'Invite Valid'}
              </span>
            </div>
            <p className="text-gray-600 text-sm">
              {inviteType === 'team' 
                ? `You've been invited to join "${teamName}" on anthrogen:drift!`
                : "You've been invited to join anthrogen:drift!"
              }
            </p>
            {inviteEmail && (
              <p className="text-gray-500 text-xs mt-2">
                Invitation sent to: {inviteEmail}
              </p>
            )}
          </div>
        </div>

        {/* Signup Options */}
        {!signupMethod ? (
          <div className="glass-card rounded-sm p-8 shadow-xl">
            <h2 className="text-xl font-bold text-gray-800 mb-6 text-center">
              {inviteType === 'team' ? 'Join the Team' : 'Create Your Account'}
            </h2>
            
            <div className="space-y-4">
              <button
                onClick={() => setSignupMethod('email')}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-sm font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
              >
                Create Account with Password
              </button>
              
              <div className="mt-4 p-4 bg-blue-50 rounded-sm border border-blue-200">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm text-blue-800 font-medium mb-1">
                      Flexible Sign-in Options
                    </p>
                    <p className="text-xs text-blue-700">
                      After creating your account, you can sign in with either your password or Google for added convenience.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {inviteType === 'team' && (
              <div className="mt-4 p-3 bg-gray-50 rounded-sm">
                <p className="text-gray-700 text-xs text-center">
                  ⚠️ Make sure to use <strong>{inviteEmail}</strong> to join the team
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Password Setup Form */
          <div className="glass-card rounded-sm p-8 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                Create Your Account
              </h2>
              <button
                onClick={() => setSignupMethod(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ← Back
              </button>
            </div>
            
            <p className="text-gray-600 mb-4">
              {inviteType === 'team' 
                ? `Create your account with ${inviteEmail} to join "${teamName}"`
                : 'Set up your account with a secure password.'
              }
            </p>

            {/* Google Sign-in Info */}
            <div className="mb-6 p-4 bg-green-50 rounded-sm border border-green-200">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm text-green-800 font-medium mb-1">
                    Future Login Flexibility
                  </p>
                  <p className="text-xs text-green-700">
                    After creating your account, you can sign in with either your password or Google using the same email address (<strong>{inviteEmail}</strong>).
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={inviteType === 'team' || !!inviteEmail}
                />
                {(inviteType === 'team' || inviteEmail) && (
                  <p className="text-xs text-gray-500 mt-1">
                    This email was specified in your invitation
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                
                {/* Password Requirements */}
                <div className="mt-3 space-y-2">
                  <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
                  
                  <div className="space-y-1">
                    <div className="flex items-center text-sm">
                      {passwordRequirements.length ? (
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                      )}
                      <span className={passwordRequirements.length ? 'text-green-700' : 'text-red-600'}>
                        At least 12 characters long
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      {passwordRequirements.uppercase ? (
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                      )}
                      <span className={passwordRequirements.uppercase ? 'text-green-700' : 'text-red-600'}>
                        At least one uppercase letter (A-Z)
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      {passwordRequirements.lowercase ? (
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                      )}
                      <span className={passwordRequirements.lowercase ? 'text-green-700' : 'text-red-600'}>
                        At least one lowercase letter (a-z)
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      {passwordRequirements.number ? (
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                      )}
                      <span className={passwordRequirements.number ? 'text-green-700' : 'text-red-600'}>
                        At least one number (0-9)
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      {passwordRequirements.special ? (
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                      )}
                      <span className={passwordRequirements.special ? 'text-green-700' : 'text-red-600'}>
                        At least one special character (!@#$%^&*)
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <button
                onClick={handleEmailSignup}
                disabled={loading || !inviteEmail.trim() || !isPasswordValid || !confirmPassword.trim() || password !== confirmPassword}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-sm font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Powered by Odyssey™ AI
          </p>
        </div>
      </div>
    </div>
  );
};